import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, PenSquare, RefreshCw, Search } from "lucide-react";
import api from "@/lib/api";
import { fmtDateTime, formatApiErrorDetail } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MailSettings } from "@/components/mail/MailSettings";
import { MailList } from "@/components/mail/MailList";
import { MailReader } from "@/components/mail/MailReader";
import { ComposeDialog } from "@/components/mail/ComposeDialog";

const quoted = (m) => `\n\n\n--- ${fmtDateTime(m.date)}, ${m.from_name} <${m.from_email}> ha scritto:\n${(m.text || "").split("\n").map((l) => `> ${l}`).join("\n")}`;

export default function Posta() {
  const [acc, setAcc] = useState(undefined);
  const [folder, setFolder] = useState("INBOX");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sel, setSel] = useState(null);
  const [msg, setMsg] = useState(null);
  const [reading, setReading] = useState(false);
  const [compose, setCompose] = useState(null);

  useEffect(() => { api.get("/mail/account").then((r) => setAcc(r.data)).catch(() => setAcc({ configured: false })); }, []);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try { setItems((await api.get("/mail/messages", { params: { folder, q, limit: 50 } })).data); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); setItems([]); }
    finally { setLoading(false); }
  }, [folder, q]);

  useEffect(() => { if (acc?.configured) { setSel(null); setMsg(null); load(); } }, [acc?.configured, folder]); // eslint-disable-line react-hooks/exhaustive-deps

  const open = async (m) => {
    setSel(m.uid); setReading(true); setMsg(null);
    try {
      setMsg((await api.get(`/mail/messages/${m.uid}`, { params: { folder } })).data);
      setItems((l) => l.map((x) => (x.uid === m.uid ? { ...x, seen: true } : x)));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setSel(null); }
    finally { setReading(false); }
  };
  const reply = () => setCompose({ to: msg.from_email, subject: /^re:/i.test(msg.subject) ? msg.subject : `Re: ${msg.subject}`, text: quoted(msg), in_reply_to: msg.message_id, references: msg.references });
  const forward = () => setCompose({ subject: /^(fwd?|i):/i.test(msg.subject) ? msg.subject : `Fwd: ${msg.subject}`, text: quoted(msg) });

  if (acc === undefined) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  if (!acc.configured) return <div className="max-w-3xl" data-testid="mail-setup"><MailSettings onSaved={setAcc} /></div>;

  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden flex flex-col h-[calc(100vh-9.5rem)] min-h-[520px]" data-testid="mail-page">
      <div className="flex items-center gap-2 flex-wrap px-3 sm:px-4 py-3 border-b border-border">
        <Tabs value={folder} onValueChange={setFolder}>
          <TabsList data-testid="mail-folder-tabs">
            <TabsTrigger value="INBOX" data-testid="mail-folder-inbox">In arrivo</TabsTrigger>
            <TabsTrigger value="SENT" data-testid="mail-folder-sent">Inviata</TabsTrigger>
          </TabsList>
        </Tabs>
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="relative flex-1 min-w-[160px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca mittente o oggetto…" className="pl-9 h-9" data-testid="mail-search-input" />
        </form>
        <Button variant="ghost" size="icon" onClick={load} disabled={loading} data-testid="mail-refresh-button"><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /></Button>
        <span className="hidden md:inline text-xs text-muted-foreground ml-auto truncate" data-no-translate>{acc.email}</span>
        <Button onClick={() => setCompose({})} className="ml-auto md:ml-0" data-testid="mail-compose-button"><PenSquare className="w-4 h-4 mr-1.5" /> Scrivi</Button>
      </div>
      <div className="flex-1 min-h-0 grid lg:grid-cols-[380px_1fr]">
        <div className={`overflow-y-auto border-r border-border ${sel ? "hidden lg:block" : ""}`}>
          <MailList items={items} loading={loading} error={error} selected={sel} folder={folder} onOpen={open} />
        </div>
        <div className={`min-h-0 flex flex-col ${sel ? "" : "hidden lg:flex"}`}>
          <MailReader msg={msg} loading={reading} folder={folder} onReply={reply} onForward={forward} onBack={() => { setSel(null); setMsg(null); }} />
        </div>
      </div>
      <ComposeDialog open={!!compose} initial={compose} onClose={() => setCompose(null)} onSent={() => folder === "SENT" && load()} />
    </div>
  );
}
